import axios from 'axios';
import { BASE_URL } from '../../../../Constants/constants';
import i18n from '../../../../i18n/i18n'; // Import i18n instance for translations

// Helper function to get translation
const t = (key, options = {}) => {
    return i18n.t(`training.validation.${key}`, options);
};

export const validateTrainingFormData = (formData) => {
    const errors = {};

    // Helper function to check empty values
    const isEmptyValue = value => {
        // Check if value is undefined, null, or an empty string after trimming
        return value === undefined || value === null ||
            (typeof value === 'string' && value.trim() === '') ||
            (typeof value === 'object' && Object.keys(value).length === 0);
    };

    // Required field validations with specific messages
    if (isEmptyValue(formData.training_name)) {
        errors.training_name = t('trainingNameRequired');
    }

    if (isEmptyValue(formData.training_status)) {
        errors.training_status = t('trainingStatusRequired');
    }

    if (isEmptyValue(formData.training_mode)) {
        errors.training_mode = t('trainingModeRequired');
    }

    // Year completed is only required when status is "Completed"
    if (formData.training_status === 'Completed' && isEmptyValue(formData.year_completed)) {
        errors.year_completed = t('yearCompletedRequired');
    }

    // Validate certificate if it exists and status is "Completed"
    if (formData.training_status === 'Completed' && formData.training_certificate) {
        const certificate = formData.training_certificate;

        // Only validate if it's a File object (new upload)
        if (certificate instanceof File) {
            if (certificate.size > 10 * 1024 * 1024) { // 10MB limit
                errors.training_certificate = t('certificateFileTooLarge');
            }

            // Validate file type (PDF, JPG, PNG, DOC, DOCX)
            if (certificate.type) {
                const allowedTypes = [
                    'application/pdf',
                    'image/jpeg',
                    'image/png',
                    'application/msword',
                    'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
                ];
                if (!allowedTypes.includes(certificate.type)) {
                    errors.training_certificate = t('certificateInvalidFileType');
                }
            }
        }
    }

    return errors;
};

// Helper function to sanitize data
export const sanitizePayload = (data) => {
    const sanitizedData = { ...data };
    Object.keys(sanitizedData).forEach((key) => {
        if (sanitizedData[key] === '') {
            sanitizedData[key] = null;
        }
    });
    // Remove file objects from sanitized data as they'll be handled separately
    delete sanitizedData.training_certificate;
    return sanitizedData;
};

// Helper function to get operations translation
const tOps = (key, options = {}) => {
    return i18n.t(`training.operations.${key}`, options);
};

// Fetch training data
export const fetchTrainingData = async (currentUser) => {
    const token = localStorage.getItem('accessToken');
    const config = {
        headers: { Authorization: `Bearer ${token}` },
    };

    try {
        const response = await axios.get(`${BASE_URL}/training/me`, config);
        return response.data || [];
    } catch (error) {
        if (error.response && error.response.status === 404) {
            return [];
        }
        console.error(tOps('errorFetchingTraining'), error);
        throw new Error(i18n.t('training.error.failedLoad'));
    }
};

// Upload training certificate (single file)
export const uploadTrainingCertificate = async (trainingId, certificate) => {
    const token = localStorage.getItem('accessToken');
    const formData = new FormData();

    // Add the certificate to FormData
    if (certificate instanceof File) {
        formData.append('certificate', certificate);
    }

    const config = {
        headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
        },
    };

    try {
        const response = await axios.post(
            `${BASE_URL}/training/${trainingId}/certificate`,
            formData,
            config
        );
        return response.data;
    } catch (error) {
        console.error(tOps('errorUploadingCertificate'), error);
        throw new Error(i18n.t('training.error.failedUploadCertificate'));
    }
};

// Delete training certificate
export const deleteTrainingCertificate = async (trainingId, certificateId) => {
    const token = localStorage.getItem('accessToken');
    const config = {
        headers: { Authorization: `Bearer ${token}` },
    };

    try {
        await axios.delete(
            `${BASE_URL}/training/${trainingId}/certificate/${certificateId}`,
            config
        );
    } catch (error) {
        console.error(tOps('errorDeletingCertificate'), error);
        throw new Error(i18n.t('training.error.failedDeleteCertificate'));
    }
};

// Download training certificate
export const downloadTrainingCertificate = async (trainingId, certificateId, filename) => {
    const token = localStorage.getItem('accessToken');
    const config = {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
    };

    try {
        const response = await axios.get(
            `${BASE_URL}/training/${trainingId}/certificate/${certificateId}/download`,
            config
        );

        // Create blob link to download
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    } catch (error) {
        console.error(tOps('errorDownloadingCertificate'), error);
        throw new Error(i18n.t('training.error.failedDownloadCertificate'));
    }
};

// Save or update training data with certificate upload
export const saveTrainingData = async (trainings, currentUser, onSave, setShowSuccessMessage, setErrors) => {
    const token = localStorage.getItem('accessToken');
    const config = {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    };

    try {
        const results = await Promise.all(
            trainings.map(async (training) => {
                const sanitizedData = sanitizePayload(training);
                let savedTraining;

                // Save or update training record
                if (training.id) {
                    const response = await axios.put(`${BASE_URL}/training/${training.id}`, sanitizedData, config);
                    savedTraining = response.data;
                } else {
                    const response = await axios.post(`${BASE_URL}/training`, sanitizedData, config);
                    savedTraining = response.data;
                }

                // Upload certificate if it exists and is a File object
                if (training.training_certificate && training.training_certificate instanceof File) {
                    await uploadTrainingCertificate(savedTraining.id, training.training_certificate);
                }

                return savedTraining;
            })
        );

        setErrors([]);
        setShowSuccessMessage(true);
        setTimeout(() => setShowSuccessMessage(false), 3000);
        onSave('training', results);
    } catch (error) {
        console.error(tOps('errorSavingTraining'), error);
        setErrors([i18n.t('training.error.failedSave')]);
    }
};